import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { AssetTypeDescEnum } from '../asset/asset.dto';
import { Description, mapComponent, mapDescription, mapPriority, translateAll } from '../des.dto';
import { AssetTypeCfg, IssueCfg, IssueComponent, LocalizedRiskLevel, Resolution, RiskLevel } from '../issue/issue.dto';

@Injectable()
export abstract class IssueCfgService {
  abstract getAssetsConfiguration(divisionId: string, organizationId: string, assetType: AssetTypeDescEnum): Promise<AssetTypeCfg>;
  abstract getIssuesConfiguration(divisionId: string, organizationId?: string): Promise<IssueCfg>;

  protected constructor(protected readonly translateService: TranslateService) {}

  static riskFromWeight(weight: number, priorities: LocalizedRiskLevel[]): LocalizedRiskLevel {
    let risk: RiskLevel;
    let length = priorities.length - 1;
    for (; length >= 0; length--) {
      if (weight >= priorities[length].weight) {
        risk = priorities[length];
        break;
      }
    }
    return risk;
  }

  getIssuePriorities(divisionId: string, organizationId: string): Observable<LocalizedRiskLevel[]> {
    return new Observable<RiskLevel[]>((observer) => {
      this.getIssuesConfiguration(divisionId, organizationId).then((cfg) => {
        const lang = this.translateService.getCurrentLang();
        const priorities = cfg.priorities.map((c) => mapPriority(c, cfg, lang));
        priorities.sort((a, b) => (a.weight < b.weight ? -1 : a.weight > b.weight ? 1 : 0));
        observer.next(priorities);

        const sub = this.translateService.onLangChange.subscribe((evt) => {
          translateAll(priorities, evt.lang);
          observer.next(priorities);
        });
        observer.add(sub);
      });
    });
  }

  async getIssuePriorityFromWeight(divisionId: string, organizationId: string, weight: number): Promise<RiskLevel> {
    const priorities = await this.getIssuePriorities(divisionId, organizationId).pipe(take(1)).toPromise();
    return IssueCfgService.riskFromWeight(weight, priorities);
  }

  getIssueComponents(divisionId: string, organizationId: string, assetType: AssetTypeDescEnum = AssetTypeDescEnum.PALLET_RACK): Observable<IssueComponent[]> {
    return new Observable<IssueComponent[]>((observer) => {
      const assetTypesToFetch = assetType
        ? [this.getAssetsConfiguration(divisionId, organizationId, assetType)]
        : [
            this.getAssetsConfiguration(divisionId, organizationId, AssetTypeDescEnum.PALLET_RACK),
            this.getAssetsConfiguration(divisionId, organizationId, AssetTypeDescEnum.CANTILEVER),
          ];

      Promise.all(assetTypesToFetch).then((configs) => {
        const lang = this.translateService.getCurrentLang();

        const components = configs
          .flatMap((config) => config.components.map((component) => mapComponent(component, config, lang)))
          .sort((a, b) => a.localized.localeCompare(b.localized));

        const uniqueComponents = Array.from(new Map(components.map((comp) => [comp.localized, comp])).values());

        observer.next(uniqueComponents);

        const langChangeSub = this.translateService.onLangChange.subscribe((evt) => {
          translateAll(uniqueComponents, evt.lang);
          observer.next(uniqueComponents);
        });

        observer.add(langChangeSub);
      });
    });
  }

  getIssueActions(divisionId: string, organizationId: string): Observable<Description<number>[]> {
    return new Observable<IssueComponent[]>((observer) => {
      this.getIssuesConfiguration(divisionId, organizationId).then((cfg) => {
        const lang = this.translateService.currentLang;
        const descriptions = cfg.actions.map((c) => mapDescription(c, cfg, lang)).sort((a, b) => a.localized.localeCompare(b.localized));
        observer.next(descriptions);

        const sub = this.translateService.onLangChange.subscribe((evt) => {
          translateAll(descriptions, evt.lang);
          observer.next(descriptions);
        });
        observer.add(sub);
      });
    });
  }

  getIssueStatuses(divisionId: string, organizationId: string): Observable<Description<number>[]> {
    return new Observable<IssueComponent[]>((observer) => {
      this.getIssuesConfiguration(divisionId, organizationId).then((cfg) => {
        const lang = this.translateService.currentLang;
        const statuses = cfg.statuses.map((c) => mapDescription(c, cfg, lang)).sort((a, b) => a.localized.localeCompare(b.localized));
        observer.next(statuses);

        const sub = this.translateService.onLangChange.subscribe((evt) => {
          translateAll(statuses, evt.lang);
          observer.next(statuses);
        });
        observer.add(sub);
      });
    });
  }

  async getIssueDescriptions(divisionId: string, organizationId: string, component?: string): Promise<Description<number>[]> {
    const issueCfg = await this.getIssuesConfiguration(divisionId, organizationId);
    let descriptions: Description<number>[];
    if (component) {
      descriptions = issueCfg.components[component]?.map((c) => mapDescription(c, issueCfg, this.translateService.currentLang));
    } else {
      descriptions = issueCfg.descriptions.map((c) => mapDescription(c, issueCfg, this.translateService.currentLang));
    }

    return descriptions?.sort((a, b) => a.localized.localeCompare(b.localized));
  }

  async getIssueResolvedStatus(divisionId: string, organizationId: string): Promise<Description<number>> {
    const issueCfg = await this.getIssuesConfiguration(divisionId, organizationId);
    let resolvedStatus = null;

    for (const status in issueCfg.resolutions) {
      if (Object.prototype.hasOwnProperty.call(issueCfg.resolutions, status) && issueCfg.resolutions[status] === Resolution.RESOLVED) {
        resolvedStatus = {
          _id: issueCfg.mysqlIds[status],
          description: status,
          descriptionEn: issueCfg.localized[`${status}_en`],
          descriptionFr: issueCfg.localized[`${status}_fr`],
        };
      }
    }

    return resolvedStatus;
  }
  async getIssueReportedStatus(divisionId: string, organizationId: string): Promise<Description<number>> {
    const issueCfg = await this.getIssuesConfiguration(divisionId, organizationId);

    return {
      _id: issueCfg.mysqlIds['REPORTED'],
      description: 'REPORTED',
      descriptionEn: issueCfg.localized[`REPORTED_en`],
      descriptionFr: issueCfg.localized[`REPORTED_fr`],
    };
  }
}
